import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Alert, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, Tab, Tabs, Typography } from '@mui/material';
import { MaritimeOrderInfo, OrderInfo, OrderInfoType, PackageImage, TakeImagePhase } from 'adoms-common-lib';
import React, { useEffect } from "react";
import { imageResizeAndConvertBase64 } from '../../common/ImageEditUtil';
import { APIConnector } from '../../connector/APIConnector';
import { PackageImageCard } from './PackageImageCard';

type OrderImageDialogProps = {
    isOrderImageDialogOpen: boolean
    setOrderImageDialogOpen: () => void
    displayOrderInfo: OrderInfo
    prefetchedPhaseImageState?: Record<TakeImagePhase, PackageImage[]>
    isPrefetchingMaritimeImages?: boolean
    maritimeImageErrorMessage?: string
    onPackageImageUpdated?: () => void
}

/**
 * 注文内容画像表示ダイアログ
 * @param props 
 * @returns 
 */
export const OrderImageDialog: React.FC<OrderImageDialogProps> = (props) => {

    const MAX_PACKAGE_IMAGE_COUNT_PER_PHASE = 3;

    const TAKE_IMAGE_PHASE_ITEMS: { phase: TakeImagePhase, label: string }[] = [
        { phase: TakeImagePhase.Received, label: '受領時' },
        { phase: TakeImagePhase.Packed, label: '梱包時' },
        { phase: TakeImagePhase.Loaded, label: '搭載時' },
    ];

    const createEmptyPhaseImageState = (): Record<TakeImagePhase, PackageImage[]> => ({
        [TakeImagePhase.Received]: [],
        [TakeImagePhase.Packed]: [],
        [TakeImagePhase.Loaded]: []
    });

    const isMaritimeOrder = props.displayOrderInfo?.type === OrderInfoType.MaritimeOrderInfo;

    const [orderReceiptImageBase64, setOrderReceiptImageBase64] = React.useState<string>('');
    const [selectedPhase, setSelectedPhase] = React.useState<TakeImagePhase>(TakeImagePhase.Received);
    const [phaseImageState, setPhaseImageState] = React.useState<Record<TakeImagePhase, PackageImage[]>>(createEmptyPhaseImageState());
    const [errorMessage, setErrorMessage] = React.useState<string | undefined>(undefined);
    const [displayCircularProgress, setDisplayCircularProgress] = React.useState(false);
    const [deletingPathName, setDeletingPathName] = React.useState<string>('');
    const [isPreparingPackageImages, setIsPreparingPackageImages] = React.useState<boolean>(false);
    const [isUploadingPackageImages, setIsUploadingPackageImages] = React.useState<boolean>(false);
    const [isNotifyCustomerImageRegistered, setIsNotifyCustomerImageRegistered] = React.useState<boolean>(false);
    const [pendingDelete, setPendingDelete] = React.useState<{ phase: TakeImagePhase, image: PackageImage } | undefined>(undefined);
    const [pendingPhaseImageState, setPendingPhaseImageState] = React.useState<Record<TakeImagePhase, PackageImage[]>>(createEmptyPhaseImageState());
    const displayErrorMessage = isMaritimeOrder ? (errorMessage ?? props.maritimeImageErrorMessage) : errorMessage;
    const isProcessingMaritimeImages = (props.isPrefetchingMaritimeImages === true) || isPreparingPackageImages || isUploadingPackageImages;
    const hasPendingImageInSelectedPhase = (pendingPhaseImageState[selectedPhase] ?? []).length > 0;
    const hasRegisteredImageInSelectedPhase = (phaseImageState[selectedPhase] ?? []).length > 0;
    const hasAnyMaritimeImage = TAKE_IMAGE_PHASE_ITEMS.some(({ phase }) => (phaseImageState[phase] ?? []).length > 0);
    const shouldDisplayMaritimeLoading = isProcessingMaritimeImages && !hasAnyMaritimeImage;
    const selectedPhaseConfirmedAt = (props.displayOrderInfo as MaritimeOrderInfo)?.packageImageConfirmationByPhase?.[selectedPhase]?.confirmedAt;
    const isMobile = /iPhone|Android.+Mobile/.test(navigator.userAgent);

    useEffect(() => {
        if (!props.isOrderImageDialogOpen) {
            return;
        }

        setErrorMessage(undefined);
        setDisplayCircularProgress(false);
        setOrderReceiptImageBase64('');
        setSelectedPhase(TakeImagePhase.Received);
        setDeletingPathName('');
        setIsPreparingPackageImages(false);
        setIsUploadingPackageImages(false);
        setIsNotifyCustomerImageRegistered(false);
        setPendingDelete(undefined);
        setPendingPhaseImageState(createEmptyPhaseImageState());
    }, [props.isOrderImageDialogOpen, props.displayOrderInfo.orderID]);

    const handleAddPackageImages = async (phase: TakeImagePhase, event: React.ChangeEvent<HTMLInputElement>) => {
        try {
            if (!event.target.files || event.target.files.length === 0) {
                return;
            }

            setErrorMessage(undefined);
            const currentImageCount = (phaseImageState[phase] ?? []).length;
            const pendingImageCount = (pendingPhaseImageState[phase] ?? []).length;
            const selectedFiles = Array.from(event.target.files);

            if (currentImageCount >= MAX_PACKAGE_IMAGE_COUNT_PER_PHASE) {
                setErrorMessage(`画像は1フェーズあたり最大${MAX_PACKAGE_IMAGE_COUNT_PER_PHASE}枚まで登録できます。`);
                event.target.value = '';
                return;
            }

            if (currentImageCount + pendingImageCount + selectedFiles.length > MAX_PACKAGE_IMAGE_COUNT_PER_PHASE) {
                setErrorMessage(`画像は1フェーズあたり最大${MAX_PACKAGE_IMAGE_COUNT_PER_PHASE}枚まで登録できます。`);
                event.target.value = '';
                return;
            }

            if (!selectedFiles.every(file => file.type === 'image/jpeg' || file.type === 'image/png')) {
                setErrorMessage('このファイルは送信できません。jpg/png形式の画像ファイルのみ送信可能です。');
                event.target.value = '';
                return;
            }

            setIsPreparingPackageImages(true);
            const resizedImageList = await Promise.all(selectedFiles.map(file => imageResizeAndConvertBase64(file)));
            const pendingImages = resizedImageList.map((image) => ({
                pathName: '',
                imageBase64: image.imageBase64,
            }));

            setPendingPhaseImageState(prev => ({
                ...prev,
                [phase]: [
                    ...(prev[phase] ?? []),
                    ...pendingImages
                ]
            }));
            event.target.value = '';
        } catch (error) {
            console.log(error);
            setErrorMessage('画像を選択できませんでした。');
        } finally {
            setIsPreparingPackageImages(false);
        }
    };

    const handleRegisterPackageImages = async (phase: TakeImagePhase) => {
        const pendingImages = pendingPhaseImageState[phase] ?? [];
        if (pendingImages.length === 0) {
            return;
        }

        setErrorMessage(undefined);
        setIsUploadingPackageImages(true);

        const imageBase64List = pendingImages.map((image) => image.imageBase64);
        const apiConnector: APIConnector = APIConnector.instance;
        await apiConnector.putPackageImage(
            undefined,
            props.displayOrderInfo.orderID,
            phase,
            imageBase64List,
            isNotifyCustomerImageRegistered
        ).then(() => {
            // 登録成功したら、pending→登録済みに移動
            setPhaseImageState(prev => ({
                ...prev,
                [phase]: [
                    ...(prev[phase] ?? []),
                    ...pendingImages
                ]
            }));

            // pendingはクリア
            setPendingPhaseImageState(prev => ({
                ...prev,
                [phase]: []
            }));

            setIsNotifyCustomerImageRegistered(false);

            props.onPackageImageUpdated?.();
        }).catch(error => {
            if (
                error.response
                && error.response.data.reasonCode == "PPI0004") {
                setErrorMessage('画像は登録されましたが、メール送信に失敗しました。');
            } else {
                setErrorMessage('画像を登録できませんでした。');
            }
        }).finally(() => {
            setIsUploadingPackageImages(false);
        });
    };

    // 登録前の選択画像を削除
    const handleRemovePendingPackageImage = (phase: TakeImagePhase, index: number) => {
        setPendingPhaseImageState(prev => ({
            ...prev,
            [phase]: (prev[phase] ?? []).filter((_, targetIndex) => targetIndex !== index)
        }));
    };

    useEffect(() => {
        if (!isMaritimeOrder) {
            return;
        }

        setPhaseImageState(props.prefetchedPhaseImageState ?? createEmptyPhaseImageState());
    }, [isMaritimeOrder, props.prefetchedPhaseImageState]);

    useEffect(() => {
        const fetchOrderImage = async () => {
            if (!props.isOrderImageDialogOpen) {
                return;
            }

            const apiConnector: APIConnector = APIConnector.instance;

            if (isMaritimeOrder) {
                return;
            }

            if (props.displayOrderInfo.receiptFileName) {
                setDisplayCircularProgress(true);
                await apiConnector.getReciept(props.displayOrderInfo.orderID, props.displayOrderInfo.receiptFileName).then(response => {
                    setOrderReceiptImageBase64(response);
                    setErrorMessage(undefined);
                    setDisplayCircularProgress(false);
                }).catch(error => {
                    setErrorMessage("注文内容が取得できませんでした。");
                    console.log(error);
                    setDisplayCircularProgress(false);
                });
            }
        };

        fetchOrderImage();
    }, [props.isOrderImageDialogOpen, props.displayOrderInfo.orderID, props.displayOrderInfo.receiptFileName, isMaritimeOrder]);

    const handleDeletePackageImage = async (phase: TakeImagePhase, image: PackageImage) => {
        if (!image.pathName) {
            return;
        }

        try {
            setDeletingPathName(image.pathName);
            setErrorMessage(undefined);

            const apiConnector: APIConnector = APIConnector.instance;
            await apiConnector.deletePackageImage(image.pathName);

            setPhaseImageState(prev => ({
                ...prev,
                [phase]: (prev[phase] ?? []).filter(target => target.pathName !== image.pathName)
            }));
            props.onPackageImageUpdated?.();
        } catch (error) {
            console.log(error);
            setErrorMessage('画像を削除できませんでした。');
        } finally {
            setDeletingPathName('');
        }
    };

    const handleOpenDeleteConfirm = (phase: TakeImagePhase, image: PackageImage) => {
        setPendingDelete({ phase, image });
    };

    const handleCloseDeleteConfirm = () => {
        if (deletingPathName) {
            return;
        }
        setPendingDelete(undefined);
    };

    const handleConfirmDeletePackageImage = async () => {
        if (!pendingDelete) {
            return;
        }

        await handleDeletePackageImage(pendingDelete.phase, pendingDelete.image);
        setPendingDelete(undefined);
    };

    return (
        <React.Fragment>
            <Dialog
                open={props.isOrderImageDialogOpen}
                onClose={props.setOrderImageDialogOpen}
            >
                <DialogTitle id="dialog-title">
                    注文内容
                </DialogTitle>
                <DialogContent
                    sx={{
                        paddingBottom: 0,
                        minWidth: 300,
                        minHeight: 100,
                        textAlign: "center"
                    }}>
                    {displayErrorMessage ?
                        <Alert severity="error" >{displayErrorMessage}</Alert>
                        : undefined
                    }
                    {(isMaritimeOrder ? shouldDisplayMaritimeLoading : displayCircularProgress) ?
                        <Grid item xs={12} textAlign={"center"}>
                            <CircularProgress />
                        </Grid>
                        :
                        <React.Fragment>
                            {isMaritimeOrder ?
                                <Grid container spacing={2} direction="column">
                                    <Grid item>
                                        <Tabs
                                            value={selectedPhase}
                                            onChange={(_, value: TakeImagePhase) => setSelectedPhase(value)}
                                            variant="fullWidth"
                                        >
                                            {TAKE_IMAGE_PHASE_ITEMS.map((phaseItem) => (
                                                <Tab
                                                    key={phaseItem.phase}
                                                    value={phaseItem.phase}
                                                    label={phaseItem.label}
                                                />
                                            ))}
                                        </Tabs>
                                    </Grid>
                                    <Grid item>
                                        <Grid container alignItems="center" justifyContent="space-between">
                                            <Grid item>
                                                {hasRegisteredImageInSelectedPhase ?
                                                    (selectedPhaseConfirmedAt ?
                                                        <Typography color="success.main" sx={{ lineHeight: 1.2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <CheckCircleIcon sx={{ fontSize: 16 }} />
                                                            {selectedPhaseConfirmedAt} 確認済み
                                                        </Typography>
                                                        : <Typography color="warning.main" sx={{ lineHeight: 1.2 }}>
                                                            未確認
                                                        </Typography>)
                                                    : null}
                                            </Grid>
                                            <Grid item textAlign={'right'}>
                                                <label htmlFor={`btn-upload-${props.displayOrderInfo.orderID}-${selectedPhase}`}>
                                                    <input
                                                        id={`btn-upload-${props.displayOrderInfo.orderID}-${selectedPhase}`}
                                                        name={`btn-upload-${props.displayOrderInfo.orderID}-${selectedPhase}`}
                                                        style={{ display: 'none' }}
                                                        type="file"
                                                        accept="image/jpeg,image/png"
                                                        multiple
                                                        onChange={(event) => handleAddPackageImages(selectedPhase, event)} />
                                                    <Button
                                                        component="span"
                                                        disabled={
                                                            isProcessingMaritimeImages ||
                                                            deletingPathName !== '' ||
                                                            ((phaseImageState[selectedPhase] ?? []).length + (pendingPhaseImageState[selectedPhase] ?? []).length) >= MAX_PACKAGE_IMAGE_COUNT_PER_PHASE
                                                        }
                                                        color="primary"
                                                        variant="outlined"
                                                        startIcon={<AddPhotoAlternateIcon />}
                                                    >
                                                        追加
                                                    </Button>
                                                </label>
                                                <Button
                                                    sx={{ ml: 1 }}
                                                    onClick={() => handleRegisterPackageImages(selectedPhase)}
                                                    disabled={
                                                        isProcessingMaritimeImages ||
                                                        deletingPathName !== '' ||
                                                        (pendingPhaseImageState[selectedPhase] ?? []).length === 0
                                                    }
                                                    color="primary"
                                                    variant="contained"
                                                >
                                                    登録
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    {hasPendingImageInSelectedPhase ?
                                        <Grid item>
                                            <Alert severity="info">選択済み画像を確認後、「登録」を押してください。</Alert>
                                        </Grid>
                                        : undefined
                                    }
                                    <Grid item>
                                        {hasPendingImageInSelectedPhase ?
                                            <React.Fragment>
                                                <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                                                    <Grid item>
                                                        <Typography variant="subtitle2" sx={{ textAlign: 'left', color: 'warning.main' }}>
                                                            選択済み（未登録）
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={isNotifyCustomerImageRegistered}
                                                                    onChange={(event) => setIsNotifyCustomerImageRegistered(event.target.checked)}
                                                                    disabled={isProcessingMaritimeImages || deletingPathName !== ''}
                                                                />
                                                            }
                                                            label="お客様に通知する"
                                                        />
                                                    </Grid>
                                                </Grid>
                                                <Grid container spacing={2} direction="column" sx={{ mb: 2 }}>
                                                    {(pendingPhaseImageState[selectedPhase] ?? []).map((image, index) => (
                                                        <Grid item key={`pending-${selectedPhase}-${index}`} textAlign={'center'}>
                                                            <PackageImageCard
                                                                imageBase64={image.imageBase64}
                                                                isMobile={isMobile}
                                                                statusLabel={hasPendingImageInSelectedPhase ? '未登録' : undefined}
                                                                statusColor={hasPendingImageInSelectedPhase ? 'warning.main' : undefined}
                                                                onDelete={() => handleRemovePendingPackageImage(selectedPhase, index)}
                                                                deleteDisabled={isProcessingMaritimeImages}
                                                            />
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </React.Fragment>
                                            : undefined
                                        }
                                        {(phaseImageState[selectedPhase] ?? []).length > 0 ?
                                            <React.Fragment>
                                                {hasPendingImageInSelectedPhase ?
                                                    <Typography variant="subtitle2" sx={{ textAlign: 'left', mb: 1, color: 'success.main' }}>
                                                        登録済み
                                                    </Typography>
                                                    : undefined
                                                }
                                                <Grid container spacing={2} direction="column">
                                                    {(phaseImageState[selectedPhase] ?? []).map((image, index) => (
                                                        <Grid item key={`${selectedPhase}-${index}`} textAlign={'center'}>
                                                            <PackageImageCard
                                                                imageBase64={image.imageBase64}
                                                                isMobile={isMobile}
                                                                statusLabel={hasPendingImageInSelectedPhase ? '登録済み' : undefined}
                                                                statusColor={hasPendingImageInSelectedPhase ? 'success.main' : undefined}
                                                                onDelete={() => handleOpenDeleteConfirm(selectedPhase, image)}
                                                                deleteDisabled={!image.pathName || deletingPathName === image.pathName}
                                                            />
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </React.Fragment>
                                            :
                                            (hasPendingImageInSelectedPhase ?
                                                undefined
                                                :
                                                <Alert severity="info">該当フェーズの画像がありません。</Alert>
                                            )
                                        }
                                    </Grid>
                                </Grid>
                                :
                                orderReceiptImageBase64 ?
                                    <img
                                        id="receiptImage"
                                        src={orderReceiptImageBase64}
                                        style={
                                            navigator.userAgent.match(/iPhone|Android.+Mobile/) ?
                                                { width: '100%' } :
                                                { width: '50%' }
                                        }
                                    />
                                    :
                                    <Alert severity="info">画像がありません。</Alert>
                            }
                        </React.Fragment>
                    }
                </DialogContent>
                <DialogActions>
                    <Button
                        color="primary"
                        onClick={props.setOrderImageDialogOpen}>
                        閉じる
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={pendingDelete != null}
                onClose={handleCloseDeleteConfirm}
            >
                <DialogTitle>画像削除確認</DialogTitle>
                <DialogContent>
                    この画像を削除しますか？
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCloseDeleteConfirm}
                        disabled={deletingPathName !== ''}
                    >
                        キャンセル
                    </Button>
                    <Button
                        color='error'
                        onClick={handleConfirmDeletePackageImage}
                        disabled={deletingPathName !== ''}
                    >
                        削除
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
};